// oxlint-disable-next-line sort-imports
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
  type PropsWithChildren,
} from 'react';
import camelcaseKeys from 'camelcase-keys';
import type { Jsonifiable } from 'type-fest';
import type { LDClient } from 'launchdarkly-react-client-sdk';

interface LDWindow {
  __LD__?: {
    _client?: LDClient;
    isLocal: boolean;
  };
}

export type FlagSet = Record<string, NonNullable<Jsonifiable>>;

/**
 * Holds the camelCase-keyed proxy built by {@link createFlagsProxy}, not the raw LaunchDarkly
 * payload — read it through {@link useFlags}. Exported only so a component can detect whether a
 * {@link FlagsProvider} already sits above it; `null` means there is none.
 */
export const FlagsContext = createContext<FlagSet | null>(null);

interface FlagsProviderProps extends PropsWithChildren {
  flags: FlagSet;
}

/**
 * Builds the camelCase view of a flag set, wrapped in the proxy that reports reads to LaunchDarkly.
 *
 * Deliberately called once per flag set (see {@link FlagsProvider}) rather than per consumer per
 * render. The camelCase conversion is O(number of flags), and with hundreds of flags across
 * hundreds of `useFlags()` call sites, running it on every render was a large part of the
 * main-thread cost of a route change. Returning one shared proxy also keeps its identity stable,
 * so `useMemo`/`useEffect` dependencies on the flag set no longer invalidate on every render.
 */
function createFlagsProxy(allFlags: FlagSet): FlagSet {
  const camelcased = camelcaseKeys(allFlags);

  const reverseMap: Record<string, string> = {};
  for (const originalKey of Object.keys(allFlags)) {
    // oxlint-disable-next-line typescript/no-non-null-assertion
    const camelKey = Object.keys(camelcaseKeys({ [originalKey]: true }))[0]!;
    reverseMap[camelKey] = originalKey;
  }

  // `variation()` is called purely to report the evaluation to LaunchDarkly — the value itself
  // comes from `target`. Reporting on every read overflowed LD's event queue, which made it drop
  // events, so each flag is reported at most once per flag set.
  const reportedKeys = new Set<string>();

  return new Proxy(camelcased, {
    get(target, prop) {
      // oxlint-disable-next-line typescript/no-unsafe-return
      if (typeof prop !== 'string') return Reflect.get(target, prop);

      const originalKey = reverseMap[prop];
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const ldWindow = window as unknown as LDWindow;
      const client = ldWindow.__LD__?.isLocal === false ? ldWindow.__LD__._client : undefined;

      // Nothing is marked as reported until there is a client to report to, so flags read before
      // LaunchDarkly finishes initializing are still reported once it is available.
      if (originalKey !== undefined && client && !reportedKeys.has(originalKey)) {
        // Marked before the call so a throwing client is not retried on every subsequent read.
        reportedKeys.add(originalKey);
        try {
          client.variation(originalKey);
        } catch (error) {
          // oxlint-disable-next-line no-console
          console.warn('Failed to report LaunchDarkly variation', error);
        }
      }

      return target[prop];
    },
  });
}

export function FlagsProvider({ children, flags }: FlagsProviderProps): ReactNode {
  const resolvedFlags = useMemo(() => createFlagsProxy(flags), [flags]);

  return <FlagsContext.Provider value={resolvedFlags}>{children}</FlagsContext.Provider>;
}

export const useFlags = (): FlagSet => {
  const resolvedFlags = useContext(FlagsContext);

  if (!resolvedFlags) {
    throw new Error('useFlags must be used within a FlagsProvider');
  }

  return resolvedFlags;
};

export function getVariationDetail<T>(flagKey: string, defaultValue: T) {
  // oxlint-disable-next-line typescript/consistent-type-assertions
  const ldWindow = window as unknown as LDWindow;
  const client = ldWindow.__LD__?._client;
  if (!client) {
    return { value: defaultValue, reason: undefined, variationIndex: undefined };
  }
  const detail = client.variationDetail(flagKey, defaultValue);
  return {
    value: detail.value as T,
    reason: detail.reason ?? undefined,
    variationIndex: detail.variationIndex ?? undefined,
  };
}

export function useLdIdentify(): (
  context: Record<string, unknown>,
  customData?: Record<string, unknown>
) => Promise<void> {
  return useCallback(async (context, customData) => {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const ldWindow = window as unknown as LDWindow;
    const client = ldWindow.__LD__?._client;
    if (!client) return;
    try {
      // LDClient.identify accepts an LDContext; we pass the minimal shape the
      // pricing routes need (userId + customData).
      // hostname is always included so flag rules that target by hostname
      // (e.g. feature--boolean-single-kyc) keep working after identify().
      await client.identify({
        kind: 'user',
        key: context.userId as string,
        hostname: window.location.hostname,
        ...customData,
      });
    } catch {
      // non-fatal — LD identify failures must not break the pricing page
    }
  }, []);
}
